import { useAuthStore } from '../stores/authStore';

/**
 * Get the organization timezone from the auth store.
 * Falls back to browser's local timezone if not available.
 */
function getOrgTimezone() {
  try {
    const user = useAuthStore.getState().user;
    const tz = user?.organization?.timezone;
    if (tz) {
      // Validate the timezone string
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return tz;
    }
  } catch {
    // Invalid timezone string, fall back
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a date/datetime string using the organization's timezone.
 * This replaces direct use of toLocaleString() which uses the browser's timezone.
 *
 * @param {string|Date} date - ISO date string or Date object
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date string in org timezone
 */
export function formatDateTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleString(undefined, {
    timeZone: tz,
    ...options,
  });
}

/**
 * Format just the date portion using the organization's timezone.
 */
export function formatDate(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleDateString(undefined, {
    timeZone: tz,
    ...options,
  });
}

/**
 * Format just the time portion using the organization's timezone.
 */
export function formatTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleTimeString(undefined, {
    timeZone: tz,
    ...options,
  });
}

/**
 * Get the short timezone abbreviation (e.g., "SGT", "IST") for a given IANA timezone.
 * Falls back to UTC offset (e.g., "UTC+8") if no abbreviation is available.
 */
function getTimezoneAbbr(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || tz;
  } catch {
    return tz;
  }
}

/**
 * Get dual-time display info: site time + user's local time.
 * Returns { siteTime, siteLabel, localTime, localLabel, isSameZone }.
 */
export function getDualTimeInfo(date) {
  const d = date ? new Date(date) : new Date();
  if (isNaN(d.getTime())) return null;

  const siteTimezone = getOrgTimezone();
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isSameZone = siteTimezone === localTimezone;

  const timeFormat = { hour: '2-digit', minute: '2-digit', hour12: true };

  const siteTime = d.toLocaleTimeString('en-US', { timeZone: siteTimezone, ...timeFormat });
  const siteLabel = getTimezoneAbbr(siteTimezone);

  const localTime = d.toLocaleTimeString('en-US', { timeZone: localTimezone, ...timeFormat });
  const localLabel = getTimezoneAbbr(localTimezone);

  return { siteTime, siteLabel, localTime, localLabel, isSameZone };
}
